import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const Logo = () => (
  <a href="#" onClick={(e) => { e.preventDefault(); scrollToId("top"); }} data-testid="nav-logo" className="flex items-center gap-3 group">
    <span className="flex items-center justify-center rounded-xl bg-gradient-to-br from-white via-[#F7F2E7] to-[#EDE4CF] px-2 py-1 ring-1 ring-gold/50 shadow-[0_0_20px_rgba(212,175,55,0.35)] transition-transform duration-500 group-hover:scale-[1.04]">
      <img src="/assets/logo-emblem.png" alt="Bridge Intell" className="h-8 w-auto" />
    </span>
    <span className="font-serif text-lg tracking-[0.14em] uppercase text-slate-100 group-hover:text-gold transition-colors duration-300">
      Bridge <span className="text-gold">Intell</span>
    </span>
  </a>
);

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["bridge", t.nav.bridge],
    ["directions", t.nav.directions],
    ["stakeholders", t.nav.stakeholders],
    ["method", t.nav.method],
    ["sectors", t.nav.sectors],
    ["opportunities", t.nav.opportunities],
  ];

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${scrolled ? "bg-obsidian/85 backdrop-blur-xl border-b hairline" : "bg-transparent border-b border-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[70px] flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-links">
          {links.map(([id, label]) => (
            <button key={id} data-testid={`nav-link-${id}`} onClick={() => go(id)}
              className="text-[13px] tracking-wide text-slate-300 hover:text-gold transition-colors duration-300">
              {label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <div className="flex items-center border hairline rounded-full p-0.5" data-testid="lang-switcher">
            {["fr", "en"].map((l) => (
              <button key={l} data-testid={`lang-switcher-${l}`} onClick={() => setLang(l)}
                className={`px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.2em] transition-colors duration-300 ${lang === l ? "bg-gold text-obsidian" : "text-slate-400 hover:text-slate-100"}`}>
                {l}
              </button>
            ))}
          </div>
          <button data-testid="nav-cta-contact" onClick={() => go("contact")}
            className="hidden md:inline-flex px-5 py-2 border border-gold/60 text-gold text-[13px] tracking-wide rounded-full hover:bg-gold hover:text-obsidian transition-colors duration-300">
            {t.nav.contact}
          </button>
          <button data-testid="nav-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-slate-200 p-2" aria-label="Menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-obsidian/95 backdrop-blur-xl border-b hairline overflow-hidden" data-testid="nav-mobile">
            <div className="px-6 py-6 flex flex-col gap-4">
              {[...links, ["contact", t.nav.contact]].map(([id, label]) => (
                <button key={id} data-testid={`nav-mobile-${id}`} onClick={() => go(id)}
                  className="text-left text-base text-slate-200 hover:text-gold transition-colors duration-300">
                  {label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
